import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkle,
  LogOut,
  Mail,
  Images,
  FileText,
  KeyRound,
  Loader2,
  Trash2,
  Plus,
  Upload,
  Save,
  X,
  Copy,
} from "lucide-react";
import { useAuth, formatApiError } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { id: "highlights", label: "NAS.IO Era" },
  { id: "hotmart", label: "Hotmart Era" },
  { id: "brands", label: "Brands" },
];

const CONTENT_FIELDS = [
  { key: "hero.headline_1", label: "Hero — Line 1", placeholder: "Authority." },
  { key: "hero.headline_2", label: "Hero — Line 2 (italic)", placeholder: "Visibility." },
  { key: "hero.headline_3", label: "Hero — Line 3", placeholder: "Strategic" },
  { key: "hero.headline_4", label: "Hero — Line 4", placeholder: "Growth." },
  { key: "hero.subtitle", label: "Hero — Subtitle", placeholder: "NOVA Media PR builds premium visibility…", multiline: true },
  { key: "hero.trust", label: "Hero — Trust italic", placeholder: "Your brand deserves to be seen, heard, and recognized." },
  { key: "about.intro", label: "About — Meet Denisse line", placeholder: "", multiline: true },
  { key: "contact.headline", label: "Contact — Headline", placeholder: "Let's build the visibility your brand deserves.", multiline: true },
];

export default function AdminDashboard() {
  const { user, loading, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const onLogout = () => {
    logout();
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  return (
    <main
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#050505] text-white"
    >
      {/* Header */}
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30 backdrop-blur">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="/assets/nova-logo.png"
              alt="NOVA MEDIA"
              className="h-6 md:h-7 w-auto"
              draggable={false}
            />
            <span className="hidden md:inline text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5]">
              ✦ Admin console
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline text-xs text-white/60">{user.email}</span>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/70 hover:text-[#7FB3D5] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center gap-1 md:gap-2 overflow-x-auto">
          <TabButton active={tab === "leads"} onClick={() => setTab("leads")} icon={Mail} label="Leads" testid="admin-tab-leads" />
          <TabButton active={tab === "gallery"} onClick={() => setTab("gallery")} icon={Images} label="Photos" testid="admin-tab-gallery" />
          <TabButton active={tab === "content"} onClick={() => setTab("content")} icon={FileText} label="Content" testid="admin-tab-content" />
          <TabButton active={tab === "password"} onClick={() => setTab("password")} icon={KeyRound} label="Password" testid="admin-tab-password" />
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-8 md:py-12">
        {tab === "leads" && <LeadsPanel authHeaders={authHeaders} />}
        {tab === "gallery" && <GalleryPanel authHeaders={authHeaders} />}
        {tab === "content" && <ContentPanel authHeaders={authHeaders} />}
        {tab === "password" && <PasswordPanel authHeaders={authHeaders} />}
      </div>
    </main>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-3 text-[10px] uppercase tracking-[0.3em] whitespace-nowrap border-b-2 transition-colors ${
        active
          ? "border-[#7FB3D5] text-[#7FB3D5]"
          : "border-transparent text-white/50 hover:text-white"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

/* ---------------- Leads ---------------- */
function LeadsPanel({ authHeaders }) {
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: authHeaders() });
      setLeads(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: authHeaders() });
      toast.success("Lead removed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    toast.success(`Copied ${email}`);
  };

  return (
    <section data-testid="admin-leads-panel">
      <PanelHeader
        eyebrow="Contact form"
        title="Leads inbox"
        subtitle={leads ? `${leads.length} submission${leads.length === 1 ? "" : "s"}` : ""}
      />
      {error && <ErrorBox message={error} />}
      {!leads && !error && <LoadingBox />}
      {leads && leads.length === 0 && (
        <div className="border border-white/10 bg-[#0a0a0a] p-10 text-center text-white/60">
          No submissions yet. When someone books a consultation, it will appear here.
        </div>
      )}
      {leads && leads.length > 0 && (
        <div className="border border-white/10 bg-[#0a0a0a] divide-y divide-white/10">
          {leads.map((lead) => (
            <article
              key={lead.id}
              data-testid={`admin-lead-${lead.id}`}
              className="p-5 md:p-7 flex flex-col md:flex-row md:items-start gap-4"
            >
              <div className="flex-1">
                <div className="flex flex-wrap items-baseline gap-3">
                  <h3 className="font-display text-xl md:text-2xl font-light">{lead.name}</h3>
                  <button
                    onClick={() => copyEmail(lead.email)}
                    className="inline-flex items-center gap-1.5 text-xs text-[#7FB3D5] hover:text-white transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    {lead.email}
                  </button>
                  {lead.company && <span className="text-xs text-white/50">· {lead.company}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] uppercase tracking-[0.25em] text-white/50">
                  {lead.service && <span>{lead.service}</span>}
                  {lead.budget && <span>· {lead.budget}</span>}
                  <span>· {new Date(lead.created_at).toLocaleString()}</span>
                </div>
                <p className="mt-4 text-sm text-white/75 leading-relaxed whitespace-pre-wrap">
                  {lead.message}
                </p>
              </div>
              <div className="flex md:flex-col gap-2 shrink-0">
                <a
                  href={`mailto:${lead.email}?subject=RE:%20Your%20NOVA%20Media%20PR%20consultation%20request`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-[#7FB3D5] text-[10px] uppercase tracking-[0.25em] text-white hover:bg-[#7FB3D5] hover:text-black transition-colors"
                >
                  Reply
                </a>
                <button
                  onClick={() => removeLead(lead.id)}
                  data-testid={`admin-delete-lead-${lead.id}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-white/20 text-[10px] uppercase tracking-[0.25em] text-white/70 hover:border-red-500 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------------- Gallery ---------------- */
function GalleryPanel({ authHeaders }) {
  const [section, setSection] = useState(SECTIONS[0].id);
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ src: "", alt: "", caption: "", location: "", order: 0 });

  const load = useCallback(async () => {
    setItems(null);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/gallery/${section}`);
      setItems(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [section]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await axios.post(`${API}/uploads`, body, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, src: data.data_uri }));
      toast.success("Image uploaded — ready to save.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!form.src.trim()) {
      toast.error("Add an image URL or upload a file first.");
      return;
    }
    try {
      await axios.post(
        `${API}/gallery`,
        { ...form, section, order: Number(form.order) || 0 },
        { headers: authHeaders() }
      );
      toast.success("Photo added.");
      setForm({ src: "", alt: "", caption: "", location: "", order: 0 });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await axios.delete(`${API}/gallery/${id}`, { headers: authHeaders() });
      toast.success("Photo removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section data-testid="admin-gallery-panel">
      <PanelHeader
        eyebrow="Manage photos"
        title="Gallery editor"
        subtitle="Add, edit or remove photos across site sections."
      />

      <div className="flex flex-wrap gap-2 mb-8">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            data-testid={`gallery-section-${s.id}`}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.3em] border transition-colors ${
              section === s.id
                ? "border-[#7FB3D5] text-[#7FB3D5] bg-[#7FB3D5]/5"
                : "border-white/15 text-white/60 hover:text-white"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Add form */}
      <form
        onSubmit={addItem}
        data-testid="admin-gallery-form"
        className="border border-white/10 bg-[#0a0a0a] p-6 md:p-8 mb-10 space-y-5"
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-3">
              Image URL or upload
            </label>
            <input
              type="text"
              value={form.src}
              onChange={(e) => setForm({ ...form, src: e.target.value })}
              placeholder="https://... or upload below"
              data-testid="gallery-input-src"
              className="w-full bg-transparent text-white placeholder:text-white/25 border-b border-white/20 focus:border-[#7FB3D5] outline-none pb-3 font-light"
            />
            <label className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-[#7FB3D5] text-[10px] uppercase tracking-[0.3em] text-white/70 hover:text-[#7FB3D5] cursor-pointer transition-colors">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {uploading ? "Uploading" : "Upload file"}
              <input type="file" accept="image/*" hidden onChange={handleFile} data-testid="gallery-input-file" />
            </label>
          </div>
          <FormField label="Caption" value={form.caption} onChange={(v) => setForm({ ...form, caption: v })} testid="gallery-input-caption" placeholder="On stage · NAS.IO" />
          <FormField label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="gallery-input-location" placeholder="Mexico City" />
          <FormField label="Alt text" value={form.alt} onChange={(v) => setForm({ ...form, alt: v })} testid="gallery-input-alt" placeholder="Denisse on stage…" />
          <FormField label="Order" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} testid="gallery-input-order" placeholder="0" />
        </div>
        {form.src && (
          <div className="pt-2">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Preview</p>
            <img src={form.src} alt="preview" className="max-h-40 border border-white/10" />
          </div>
        )}
        <button
          type="submit"
          data-testid="gallery-add-submit"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#7FB3D5] text-black text-[10px] uppercase tracking-[0.3em] hover:bg-white transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add photo
        </button>
      </form>

      {/* List */}
      {error && <ErrorBox message={error} />}
      {!items && !error && <LoadingBox />}
      {items && items.length === 0 && (
        <div className="border border-white/10 bg-[#0a0a0a] p-8 text-center text-white/50 text-sm">
          No photos yet in this section. Add one above.
        </div>
      )}
      {items && items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`gallery-item-${it.id}`}
              className="border border-white/10 bg-[#0a0a0a] overflow-hidden group"
            >
              <div className="aspect-[4/3] overflow-hidden bg-[#050505]">
                <img src={it.src} alt={it.alt || it.caption} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 space-y-1">
                <p className="text-xs text-white truncate">{it.caption || "(no caption)"}</p>
                <p className="text-[9px] uppercase tracking-[0.3em] text-[#7FB3D5]/80 truncate">
                  {it.location || "—"} · order {it.order}
                </p>
                <button
                  onClick={() => removeItem(it.id)}
                  data-testid={`gallery-remove-${it.id}`}
                  className="mt-2 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.3em] text-white/50 hover:text-red-500"
                >
                  <Trash2 className="w-3 h-3" />
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------------- Content ---------------- */
function ContentPanel({ authHeaders }) {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((r) => setValues(r.data || {}))
      .finally(() => setLoading(false));
  }, []);

  const save = async (key) => {
    setSaving(key);
    try {
      await axios.put(
        `${API}/content`,
        { key, value: values[key] || "" },
        { headers: authHeaders() }
      );
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(null);
    }
  };

  return (
    <section data-testid="admin-content-panel">
      <PanelHeader
        eyebrow="Copy editor"
        title="Site content"
        subtitle="Edit key text blocks. Empty fields fall back to the default copy."
      />
      {loading && <LoadingBox />}
      {!loading && (
        <div className="border border-white/10 bg-[#0a0a0a] divide-y divide-white/10">
          {CONTENT_FIELDS.map((f) => (
            <div key={f.key} data-testid={`content-field-${f.key}`} className="p-5 md:p-7 grid md:grid-cols-[220px_1fr_auto] gap-4 md:gap-6 md:items-center">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#7FB3D5]">{f.label}</p>
                <p className="mt-1 text-[10px] text-white/40 font-mono">{f.key}</p>
              </div>
              {f.multiline ? (
                <textarea
                  rows={3}
                  value={values[f.key] || ""}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  data-testid={`content-input-${f.key}`}
                  className="w-full bg-transparent text-white placeholder:text-white/25 border border-white/15 focus:border-[#7FB3D5] outline-none p-3 font-light resize-none"
                />
              ) : (
                <input
                  type="text"
                  value={values[f.key] || ""}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  data-testid={`content-input-${f.key}`}
                  className="w-full bg-transparent text-white placeholder:text-white/25 border-b border-white/20 focus:border-[#7FB3D5] outline-none pb-3 font-light"
                />
              )}
              <button
                onClick={() => save(f.key)}
                disabled={saving === f.key}
                data-testid={`content-save-${f.key}`}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#7FB3D5] text-black text-[10px] uppercase tracking-[0.3em] hover:bg-white transition-colors disabled:opacity-60"
              >
                {saving === f.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Save
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------------- Password ---------------- */
function PasswordPanel({ authHeaders }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (next.length < 8) return toast.error("New password must be at least 8 characters.");
    if (next !== confirm) return toast.error("Passwords don't match.");
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: authHeaders() }
      );
      toast.success("Password updated. Next time you sign in, use the new one.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="admin-password-panel">
      <PanelHeader eyebrow="Security" title="Change password" />
      <form
        onSubmit={onSubmit}
        className="max-w-xl border border-white/10 bg-[#0a0a0a] p-6 md:p-8 space-y-5"
      >
        <PasswordField label="Current password" value={current} onChange={setCurrent} testid="password-current" />
        <PasswordField label="New password (min 8 chars)" value={next} onChange={setNext} testid="password-new" />
        <PasswordField label="Confirm new password" value={confirm} onChange={setConfirm} testid="password-confirm" />
        <button
          type="submit"
          disabled={submitting}
          data-testid="password-submit"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#7FB3D5] text-black text-[10px] uppercase tracking-[0.3em] hover:bg-white transition-colors disabled:opacity-60"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
          Update password
        </button>
      </form>
    </section>
  );
}

/* ---------------- Small pieces ---------------- */
function PanelHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="mb-8 md:mb-10">
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#7FB3D5] flex items-center gap-2">
        <Sparkle className="w-2.5 h-2.5" fill="#7FB3D5" strokeWidth={0.6} />
        {eyebrow}
      </p>
      <h2 className="mt-3 font-display text-3xl md:text-4xl font-light">{title}</h2>
      {subtitle && <p className="mt-2 text-sm text-white/55">{subtitle}</p>}
    </div>
  );
}

function FormField({ label, value, onChange, testid, placeholder, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-3">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-transparent text-white placeholder:text-white/25 border-b border-white/20 focus:border-[#7FB3D5] outline-none pb-3 font-light"
      />
    </div>
  );
}

function PasswordField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.3em] text-white/50 block mb-3">{label}</label>
      <input
        type="password"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-transparent text-white border-b border-white/20 focus:border-[#7FB3D5] outline-none pb-3 font-light"
      />
    </div>
  );
}

function LoadingBox() {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-10 flex items-center justify-center text-white/50 text-xs uppercase tracking-[0.3em]">
      <Loader2 className="w-4 h-4 animate-spin mr-3" />
      Loading
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div className="border border-red-500/40 bg-red-950/20 p-4 text-xs text-red-300 flex items-start gap-2">
      <X className="w-4 h-4 shrink-0 mt-0.5" />
      {message}
    </div>
  );
}
